import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, writeBatch } from 'firebase/firestore';
import { db, getCompanyId } from '../firebase';
import Navbar from '../components/Navbar';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const TIMES = Array.from({ length: 24 * 4 }, (_, i) => {
  const totalMinutes = i * 15;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  const displayMinute = String(minute).padStart(2, '0');
  return { label: `${displayHour}:${displayMinute} ${ampm}`, value: i, hour, minute };
});
const timeLabel = (v) => (v === 96 ? '12:00 AM' : TIMES[v]?.label);

// Max days one bulk schedule can span (keeps a typo from creating thousands of docs)
const MAX_RANGE_DAYS = 92;

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Parse 'YYYY-MM-DD' as a local date (new Date(str) would treat it as UTC)
const parseDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Monday-first weekday label for a Date
const weekdayLabel = (d) => DAYS[(d.getDay() + 6) % 7];

const prettyDate = (s) => {
  const d = parseDate(s);
  return `${weekdayLabel(d)}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

/* The Schedule tab never creates shifts. It puts existing shift templates
   (from the Shifts tab) on calendar days and assigns employees to them.
   Each placement is its own doc: { type: 'one-time', date, shiftTemplateId,
   employeeIds, ...times copied from the template } — the same shape the
   employee app already reads for clock-in checks. */
export default function Schedule() {
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  // Bulk mode: add or remove one or more templates across a date range at once
  const [showBulk, setShowBulk] = useState(false);
  const [bulkAction, setBulkAction] = useState('add'); // 'add' | 'remove'
  const [bulkTemplateIds, setBulkTemplateIds] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weekdays, setWeekdays] = useState([...DAYS]);
  const [bulkEmployees, setBulkEmployees] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const companyId = await getCompanyId();
    const [shiftsSnap, usersSnap] = await Promise.all([
      getDocs(query(collection(db, 'shifts'), where('companyId', '==', companyId))),
      getDocs(query(collection(db, 'users'), where('companyId', '==', companyId))),
    ]);
    setShifts(shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setEmployees(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.role === 'employee'));
    setLoading(false);
  };

  const templates = shifts.filter(s => s.type === 'template');

  const templateName = (t) => t.name || `${timeLabel(t.startTime)} → ${timeLabel(t.endTime)}`;

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? emp.name : 'Unknown';
  };

  // Everything happening on a 'YYYY-MM-DD': scheduled placements on that
  // date, plus legacy repeating weekly shifts that still cover its weekday.
  const shiftsOnDate = (dateStr) => {
    const day = weekdayLabel(parseDate(dateStr));
    const assignments = shifts.filter(s => s.type === 'one-time' && s.date === dateStr);
    const repeating = shifts.filter(s => s.type === 'repeating' && (s.employeesByDay || {})[day]);
    return { assignments, repeating };
  };

  const toggleBulkTemplate = (tId) => {
    setBulkTemplateIds(bulkTemplateIds.includes(tId)
      ? bulkTemplateIds.filter(t => t !== tId)
      : [...bulkTemplateIds, tId]);
  };

  const resetBulk = () => {
    setBulkAction('add');
    setBulkTemplateIds([]);
    setStartDate('');
    setEndDate('');
    setWeekdays([...DAYS]);
    setBulkEmployees([]);
    setError('');
  };

  const openBulk = () => {
    resetBulk();
    if (selectedDate) {
      setStartDate(selectedDate);
      setEndDate(selectedDate);
    }
    setSelectedDate(null);
    setShowBulk(true);
  };

  const toggleWeekday = (day) => {
    setWeekdays(weekdays.includes(day) ? weekdays.filter(d => d !== day) : [...weekdays, day]);
  };

  const toggleBulkEmployee = (empId) => {
    setBulkEmployees(bulkEmployees.includes(empId)
      ? bulkEmployees.filter(e => e !== empId)
      : [...bulkEmployees, empId]);
  };

  // Every date in [startDate, endDate] whose weekday is enabled
  const datesInRange = () => {
    if (!startDate || !endDate) return [];
    const dates = [];
    const end = parseDate(endDate);
    for (let d = parseDate(startDate); d <= end; d.setDate(d.getDate() + 1)) {
      if (weekdays.includes(weekdayLabel(d))) dates.push(fmtDate(d));
    }
    return dates;
  };

  const handleBulkSave = async (e) => {
    e.preventDefault();
    setError('');
    if (bulkTemplateIds.length === 0) { setError('Pick at least one shift to schedule'); return; }
    if (!startDate || !endDate) { setError('Pick a start and end date (type them or click days on the calendar)'); return; }
    if (parseDate(endDate) < parseDate(startDate)) { setError('End date must be on or after the start date'); return; }
    const spanDays = (parseDate(endDate) - parseDate(startDate)) / 86400000 + 1;
    if (spanDays > MAX_RANGE_DAYS) { setError(`A schedule can span at most ${MAX_RANGE_DAYS} days at a time`); return; }
    if (weekdays.length === 0) { setError('Select at least one weekday'); return; }
    const dates = datesInRange();
    if (dates.length === 0) { setError('No days in that range match the selected weekdays'); return; }

    if (bulkAction === 'remove') {
      await handleBulkRemove(dates);
      return;
    }

    setSavingBulk(true);
    try {
      const companyId = await getCompanyId();
      const chosen = templates.filter(t => bulkTemplateIds.includes(t.id));
      // Days where a template is already scheduled get the selected
      // employees merged in instead of a duplicate placement.
      const existingByKey = {};
      for (const s of shifts) {
        if (s.type === 'one-time' && s.shiftTemplateId && s.date) {
          existingByKey[`${s.shiftTemplateId}|${s.date}`] = s;
        }
      }
      // One write per (day × shift), batched under Firestore's 500-write cap
      const ops = [];
      for (const date of dates) {
        for (const template of chosen) {
          ops.push({ date, template });
        }
      }
      for (let i = 0; i < ops.length; i += 400) {
        const batch = writeBatch(db);
        for (const { date, template } of ops.slice(i, i + 400)) {
          const existing = existingByKey[`${template.id}|${date}`];
          if (existing) {
            const merged = [...new Set([...(existing.employeeIds || []), ...bulkEmployees])];
            batch.update(doc(db, 'shifts', existing.id), { employeeIds: merged, updatedAt: new Date() });
          } else {
            batch.set(doc(collection(db, 'shifts')), {
              name: template.name || '',
              type: 'one-time',
              date,
              shiftTemplateId: template.id,
              startTime: template.startTime,
              endTime: template.endTime,
              startHour: template.startHour,
              startMinute: template.startMinute,
              endHour: template.endHour,
              endMinute: template.endMinute,
              employeeIds: [...bulkEmployees],
              employeesByDay: null,
              companyId,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
          }
        }
        await batch.commit();
      }

      await loadData();
      resetBulk();
      setShowBulk(false);
    } catch (err) {
      setError(err.message);
    }
    setSavingBulk(false);
  };

  // Delete every placement of the selected templates on the matching days
  const handleBulkRemove = async (dates) => {
    const dateSet = new Set(dates);
    const toRemove = shifts.filter(s =>
      s.type === 'one-time' && s.date && dateSet.has(s.date) &&
      bulkTemplateIds.includes(s.shiftTemplateId));
    if (toRemove.length === 0) {
      setError('None of the selected shifts are scheduled on those days — nothing to remove.');
      return;
    }
    if (!window.confirm(
      `Remove ${toRemove.length} scheduled shift${toRemove.length === 1 ? '' : 's'} between ${prettyDate(startDate)} and ${prettyDate(endDate)}? Employees assigned to them will no longer be scheduled.`)) {
      return;
    }
    setSavingBulk(true);
    try {
      for (let i = 0; i < toRemove.length; i += 400) {
        const batch = writeBatch(db);
        for (const s of toRemove.slice(i, i + 400)) {
          batch.delete(doc(db, 'shifts', s.id));
        }
        await batch.commit();
      }
      await loadData();
      resetBulk();
      setShowBulk(false);
    } catch (err) {
      setError(err.message);
    }
    setSavingBulk(false);
  };

  const handleAddShift = async (template) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const companyId = await getCompanyId();
      await addDoc(collection(db, 'shifts'), {
        name: template.name || '',
        type: 'one-time',
        date: selectedDate,
        shiftTemplateId: template.id,
        startTime: template.startTime,
        endTime: template.endTime,
        startHour: template.startHour,
        startMinute: template.startMinute,
        endHour: template.endHour,
        endMinute: template.endMinute,
        employeeIds: [],
        employeesByDay: null,
        companyId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      await loadData();
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  const handleRemoveAssignment = async (assignment) => {
    const name = assignment.name || `${timeLabel(assignment.startTime)} → ${timeLabel(assignment.endTime)}`;
    if (!window.confirm(`Remove "${name}" from ${prettyDate(assignment.date)}?`)) return;
    setError('');
    try {
      await deleteDoc(doc(db, 'shifts', assignment.id));
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleEmployee = async (assignment, empId) => {
    const current = assignment.employeeIds || [];
    const next = current.includes(empId)
      ? current.filter(e => e !== empId)
      : [...current, empId];
    // Optimistic: flip locally right away, persist behind it
    setShifts(prev => prev.map(s => (s.id === assignment.id ? { ...s, employeeIds: next } : s)));
    setError('');
    try {
      await updateDoc(doc(db, 'shifts', assignment.id), { employeeIds: next, updatedAt: new Date() });
    } catch (err) {
      setError(err.message);
      await loadData();
    }
  };

  // While the bulk form is open, calendar clicks pick the range;
  // otherwise they select a day
  const handleDayClick = (dateStr) => {
    if (showBulk) {
      if (!startDate || (startDate && endDate && endDate !== startDate)) {
        setStartDate(dateStr);
        setEndDate(dateStr);
      } else if (parseDate(dateStr) < parseDate(startDate)) {
        setStartDate(dateStr);
      } else {
        setEndDate(dateStr);
      }
      return;
    }
    setSelectedDate(selectedDate === dateStr ? null : dateStr);
    setError('');
  };

  const changeMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  // Build the month grid: leading blanks (Monday-first), then each day of the month
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
  const cells = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => fmtDate(new Date(viewYear, viewMonth, i + 1))),
  ];

  const todayStr = fmtDate(today);
  const inPickedRange = (dateStr) =>
    showBulk && startDate && endDate && dateStr >= startDate && dateStr <= endDate;
  const selectedDay = selectedDate ? shiftsOnDate(selectedDate) : null;
  // Templates not on the selected day yet — the same shift doesn't need to
  // be placed twice on one date.
  const addableTemplates = selectedDay
    ? templates.filter(t => !selectedDay.assignments.some(a => a.shiftTemplateId === t.id))
    : [];

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Schedule</h1>
            <div style={styles.subtitle}>
              Click a day for quick edits, or schedule a shift across many days at once.
            </div>
          </div>
          <button
            style={styles.addButton}
            onClick={() => (showBulk ? (setShowBulk(false), resetBulk()) : openBulk())}
          >
            {showBulk ? 'Cancel' : 'Schedule Across Days'}
          </button>
        </div>

        {showBulk && (
          <div style={styles.formCard}>
            <h3 style={styles.formTitle}>Schedule across days</h3>
            <div style={styles.typeRow}>
              <button type="button"
                style={{ ...styles.typeBtn, ...(bulkAction === 'add' ? styles.typeBtnActive : {}) }}
                onClick={() => { setBulkAction('add'); setError(''); }}>
                Add shifts
              </button>
              <button type="button"
                style={{ ...styles.typeBtn, ...(bulkAction === 'remove' ? styles.typeBtnRemove : {}) }}
                onClick={() => { setBulkAction('remove'); setError(''); }}>
                Remove shifts
              </button>
            </div>
            <div style={styles.formHint}>
              {bulkAction === 'add'
                ? "Pick one or more shifts, a date range (type it below or click days on the calendar), and the weekdays they run on — they're placed on every matching day in one go. Days that already have one of these shifts just get the selected employees added."
                : 'Pick which shifts to take off the calendar, the date range, and the weekdays — every placement of those shifts on matching days is removed.'}
            </div>
            {error && <div style={styles.error}>{error}</div>}
            {templates.length === 0 ? (
              <div style={styles.hint}>No shift templates yet — create one in the Shifts tab first.</div>
            ) : (
              <form onSubmit={handleBulkSave}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Which shifts? (pick one or more)</label>
                  <div style={styles.templateRow}>
                    {templates.map(t => {
                      const on = bulkTemplateIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          style={{
                            ...styles.templateChip,
                            ...(on ? styles.templateChipSelected : {}),
                          }}
                          onClick={() => toggleBulkTemplate(t.id)}
                        >
                          {templateName(t)}
                          {t.name && <span style={styles.templateChipTime}> · {timeLabel(t.startTime)} → {timeLabel(t.endTime)}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>From</label>
                    <input type="date"
                      style={{ ...styles.input, colorScheme: 'dark' }}
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>To</label>
                    <input type="date"
                      style={{ ...styles.input, colorScheme: 'dark' }}
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>On These Weekdays</label>
                  <div style={styles.daysRow}>
                    {DAYS.map(day => (
                      <button
                        key={day}
                        type="button"
                        style={{ ...styles.dayBtn, ...(weekdays.includes(day) ? styles.dayBtnActive : {}) }}
                        onClick={() => toggleWeekday(day)}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                {bulkAction === 'add' && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Assign Employees (optional — you can also assign per day later)</label>
                    <div style={styles.employeeGrid}>
                      {employees.map(emp => {
                        const on = bulkEmployees.includes(emp.id);
                        return (
                          <div
                            key={emp.id}
                            style={{ ...styles.employeeChip, ...(on ? styles.employeeChipActive : {}) }}
                            onClick={() => toggleBulkEmployee(emp.id)}
                          >
                            <div style={{ ...styles.checkbox, ...(on ? styles.checkboxActive : {}) }}>
                              {on && '✓'}
                            </div>
                            <span style={styles.empName}>{emp.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={styles.formButtons}>
                  {datesInRange().length > 0 && parseDate(endDate) >= parseDate(startDate) && (
                    bulkAction === 'add' ? (
                      <span style={styles.rangeSummary}>
                        {datesInRange().length} day{datesInRange().length === 1 ? '' : 's'}
                        {bulkTemplateIds.length > 1 ? ` × ${bulkTemplateIds.length} shifts` : ''} will be scheduled
                      </span>
                    ) : (
                      <span style={styles.removeSummary}>
                        {(() => {
                          const dateSet = new Set(datesInRange());
                          const n = shifts.filter(s =>
                            s.type === 'one-time' && s.date && dateSet.has(s.date) &&
                            bulkTemplateIds.includes(s.shiftTemplateId)).length;
                          return `${n} scheduled shift${n === 1 ? '' : 's'} will be removed`;
                        })()}
                      </span>
                    )
                  )}
                  <button type="button" style={styles.cancelBtn}
                    onClick={() => { setShowBulk(false); resetBulk(); }}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={bulkAction === 'add' ? styles.saveBtn : styles.dangerBtn}
                    disabled={savingBulk}
                  >
                    {savingBulk
                      ? (bulkAction === 'add' ? 'Scheduling...' : 'Removing...')
                      : (bulkAction === 'add' ? 'Schedule' : 'Remove')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        <div style={styles.calendarCard}>
          <div style={styles.calHeader}>
            <div style={styles.calTitle}>{MONTHS[viewMonth]} {viewYear}</div>
            <div style={styles.calNav}>
              <button style={styles.calNavBtn} onClick={() => changeMonth(-1)}>‹</button>
              <button style={styles.calTodayBtn} onClick={goToday}>Today</button>
              <button style={styles.calNavBtn} onClick={() => changeMonth(1)}>›</button>
            </div>
          </div>

          <div style={styles.weekdayRow}>
            {DAYS.map(d => <div key={d} style={styles.weekdayCell}>{d}</div>)}
          </div>

          {loading ? (
            <div style={styles.empty}>Loading...</div>
          ) : (
            <div style={styles.grid}>
              {cells.map((dateStr, i) => {
                if (!dateStr) return <div key={`blank-${i}`} style={styles.blankCell} />;
                const { assignments, repeating } = shiftsOnDate(dateStr);
                const all = [...assignments, ...repeating];
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDate;
                const isPast = dateStr < todayStr;
                return (
                  <div
                    key={dateStr}
                    style={{
                      ...styles.dayCell,
                      ...(isPast ? styles.dayCellPast : {}),
                      ...(inPickedRange(dateStr) ? styles.dayCellRange : {}),
                      ...(isSelected ? styles.dayCellSelected : {}),
                    }}
                    onClick={() => handleDayClick(dateStr)}
                  >
                    <div style={{ ...styles.dayNumber, ...(isToday ? styles.dayNumberToday : {}) }}>
                      {Number(dateStr.slice(8))}
                    </div>
                    {all.slice(0, 3).map((s, idx) => (
                      <div
                        key={s.id + idx}
                        style={{ ...styles.shiftChip, ...(s.type === 'repeating' ? styles.shiftChipRepeating : {}) }}
                      >
                        {s.name || timeLabel(s.startTime)}
                      </div>
                    ))}
                    {all.length > 3 && <div style={styles.moreChip}>+{all.length - 3} more</div>}
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.legend}>
            <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#4f46e5' }} /> Scheduled shift</span>
            <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#2a2a4a', border: '1px solid #4f46e5' }} /> Legacy weekly shift</span>
          </div>
        </div>

        {!selectedDate && !loading && !showBulk && (
          <div style={styles.hint}>
            {templates.length === 0
              ? 'No shift templates yet — create them in the Shifts tab first, then schedule them here.'
              : 'Click a day to schedule shifts on it, or use "Schedule Across Days" to fill weeks at once.'}
          </div>
        )}

        {selectedDate && selectedDay && (
          <div style={styles.dayPanel}>
            <div style={styles.dayPanelHeader}>
              <h3 style={styles.dayPanelTitle}>{prettyDate(selectedDate)}</h3>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {selectedDay.assignments.length === 0 && selectedDay.repeating.length === 0 && (
              <div style={styles.dayPanelEmpty}>Nothing scheduled on this day yet.</div>
            )}

            {(selectedDay.assignments.length > 0 || selectedDay.repeating.length > 0) && (
              <div style={styles.columnsWrap}>
                {selectedDay.assignments.map(assignment => (
                  <div key={assignment.id} style={styles.shiftColumn}>
                    <div style={styles.columnHeader}>
                      <div style={styles.dayShiftName}>
                        {assignment.name || 'Unnamed shift'}
                      </div>
                      <div style={styles.dayShiftMeta}>
                        {timeLabel(assignment.startTime)} → {timeLabel(assignment.endTime)}
                      </div>
                      <button style={styles.deleteBtn} onClick={() => handleRemoveAssignment(assignment)}>
                        Remove from this day
                      </button>
                    </div>
                    <div style={styles.assignLabel}>Click to assign or unassign:</div>
                    {employees.length === 0 ? (
                      <div style={styles.dayPanelEmpty}>No employees yet — add them in the Employees tab.</div>
                    ) : (
                      <div style={styles.employeeColumn}>
                        {employees.map(emp => {
                          const on = (assignment.employeeIds || []).includes(emp.id);
                          return (
                            <div
                              key={emp.id}
                              style={{ ...styles.employeeChip, ...(on ? styles.employeeChipActive : {}) }}
                              onClick={() => toggleEmployee(assignment, emp.id)}
                            >
                              <div style={{ ...styles.checkbox, ...(on ? styles.checkboxActive : {}) }}>
                                {on && '✓'}
                              </div>
                              <span style={styles.empName}>{emp.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {selectedDay.repeating.map(shift => {
                  const assigned = (shift.employeesByDay || {})[weekdayLabel(parseDate(selectedDate))] || [];
                  return (
                    <div key={shift.id} style={{ ...styles.shiftColumn, opacity: 0.75 }}>
                      <div style={styles.columnHeader}>
                        <div style={styles.dayShiftName}>
                          {shift.name} <span style={styles.repeatBadge}>Legacy weekly</span>
                        </div>
                        <div style={styles.dayShiftMeta}>
                          {timeLabel(shift.startTime)} → {timeLabel(shift.endTime)}
                        </div>
                        <div style={styles.dayShiftMeta}>Managed in the Shifts tab</div>
                      </div>
                      <div style={styles.assignLabel}>Working this shift:</div>
                      {assigned.length === 0 ? (
                        <div style={styles.dayPanelEmpty}>No employees</div>
                      ) : (
                        <div style={styles.employeeColumn}>
                          {assigned.map(id => (
                            <div key={id} style={styles.employeeChip}>
                              <span style={styles.empName}>{getEmployeeName(id)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {addableTemplates.length > 0 && (
              <div style={styles.addSection}>
                <div style={styles.assignLabel}>Add a shift to this day:</div>
                <div style={styles.templateRow}>
                  {addableTemplates.map(t => (
                    <button key={t.id} style={styles.templateChip} disabled={busy} onClick={() => handleAddShift(t)}>
                      + {templateName(t)}
                      {t.name && <span style={styles.templateChipTime}> · {timeLabel(t.startTime)} → {timeLabel(t.endTime)}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {templates.length === 0 && (
              <div style={styles.hint}>
                No shift templates to add — create them in the Shifts tab first.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0f0f0f'
  },

  container: {
    padding: '32px 40px',
    maxWidth: 1200,
    margin: '0 auto'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24
  },

  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    margin: 0
  },

  subtitle: {
    color: '#999',
    fontSize: 14,
    marginTop: 4
  },

  error: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14
  },

  addButton: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 'bold',
    cursor: 'pointer'
  },

  formCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24
  },

  formTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 8
  },

  formHint: {
    color: '#999',
    fontSize: 13,
    marginBottom: 16,
    lineHeight: 1.4
  },

  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    alignItems: 'start'
  },

  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 16
  },

  label: {
    color: '#999',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1
  },

  input: {
    backgroundColor: '#2a2a2a',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    outline: 'none'
  },

  daysRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },

  dayBtn: {
    backgroundColor: '#2a2a2a',
    color: '#999',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13
  },

  dayBtnActive: {
    backgroundColor: '#4f46e5',
    color: '#fff'
  },

  rangeSummary: {
    color: '#4ade80',
    fontSize: 13,
    marginRight: 'auto'
  },

  removeSummary: {
    color: '#f87171',
    fontSize: 13,
    marginRight: 'auto'
  },

  typeRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 14,
    maxWidth: 360
  },

  typeBtn: {
    flex: 1,
    backgroundColor: '#2a2a2a',
    color: '#999',
    border: 'none',
    borderRadius: 8,
    padding: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13,
    fontFamily: 'inherit'
  },

  typeBtnActive: {
    backgroundColor: '#4f46e5',
    color: '#fff'
  },

  typeBtnRemove: {
    backgroundColor: '#7f1d1d',
    color: '#fff'
  },

  dangerBtn: {
    backgroundColor: '#b91c1c',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  formButtons: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 8
  },

  cancelBtn: {
    backgroundColor: '#2a2a2a',
    color: '#999',
    border: 'none',
    borderRadius: 10,
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  saveBtn: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  calendarCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24
  },

  calHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },

  calTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold'
  },

  calNav: {
    display: 'flex',
    gap: 8
  },

  calNavBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    width: 36,
    height: 36,
    fontSize: 18,
    cursor: 'pointer'
  },

  calTodayBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '0 16px',
    fontSize: 13,
    fontWeight: 'bold',
    cursor: 'pointer'
  },

  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 6,
    marginBottom: 6
  },

  weekdayCell: {
    color: '#999',
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
    padding: '6px 0'
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 6
  },

  blankCell: {
    minHeight: 96
  },

  dayCell: {
    backgroundColor: '#2a2a2a',
    borderRadius: 10,
    minHeight: 96,
    padding: 8,
    cursor: 'pointer',
    border: '1px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    overflow: 'hidden'
  },

  dayCellPast: {
    opacity: 0.5
  },

  dayCellRange: {
    border: '1px solid #4f46e5',
    backgroundColor: '#1a1a3e'
  },

  dayCellSelected: {
    border: '1px solid #fff'
  },

  dayNumber: {
    color: '#fff',
    fontSize: 13,
    fontWeight: 'bold',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%'
  },

  dayNumberToday: {
    backgroundColor: '#4f46e5'
  },

  shiftChip: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    borderRadius: 6,
    padding: '2px 6px',
    fontSize: 11,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  shiftChipRepeating: {
    backgroundColor: '#2a2a4a',
    border: '1px solid #4f46e5',
    color: '#c7c5f4'
  },

  moreChip: {
    color: '#999',
    fontSize: 11,
    fontWeight: 'bold'
  },

  legend: {
    display: 'flex',
    gap: 20,
    marginTop: 14
  },

  legendItem: {
    color: '#999',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },

  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 4,
    display: 'inline-block'
  },

  hint: {
    color: '#999',
    fontSize: 14,
    textAlign: 'center',
    padding: 20
  },

  dayPanel: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24
  },

  dayPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },

  dayPanelTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    margin: 0
  },

  dayPanelEmpty: {
    color: '#999',
    fontSize: 14,
    padding: '8px 0'
  },

  assignmentBlock: {
    padding: '16px 0',
    borderBottom: '1px solid #2a2a2a'
  },

  // Selected-day shifts laid out as side-by-side columns (scrolls if they overflow)
  columnsWrap: {
    display: 'flex',
    gap: 16,
    overflowX: 'auto',
    paddingBottom: 8,
    alignItems: 'flex-start'
  },

  shiftColumn: {
    flex: '0 0 240px',
    width: 240,
    backgroundColor: '#242424',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },

  columnHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    paddingBottom: 10,
    borderBottom: '1px solid #333'
  },

  employeeColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },

  assignmentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 10
  },

  dayShiftName: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 4
  },

  dayShiftMeta: {
    color: '#999',
    fontSize: 13
  },

  repeatBadge: {
    color: '#d5a94f',
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 6
  },

  assignLabel: {
    color: '#999',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 10
  },

  employeeGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10
  },

  employeeChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#2a2a2a',
    borderRadius: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    border: '1px solid transparent',
    userSelect: 'none'
  },

  employeeChipActive: {
    border: '1px solid #4f46e5',
    backgroundColor: '#1a1a3e'
  },

  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    border: '2px solid #555',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 11,
    fontWeight: 'bold'
  },

  checkboxActive: {
    backgroundColor: '#4f46e5',
    border: '2px solid #4f46e5'
  },

  empName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold'
  },

  addSection: {
    paddingTop: 16
  },

  templateRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10
  },

  templateChip: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: '1px dashed #4f46e5',
    borderRadius: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: 'inherit'
  },

  templateChipSelected: {
    backgroundColor: '#1a1a3e',
    border: '1px solid #4f46e5'
  },

  templateChipTime: {
    color: '#999',
    fontWeight: 'normal',
    fontSize: 13
  },

  deleteBtn: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13
  },

  empty: {
    color: '#999',
    textAlign: 'center',
    padding: 40
  }
};
